"use strict";
// Shared rule for the capitalizable project clustering "matching target" status.
// Both the server (sync pipeline, capitalization determination) and the web-app
// (project list/drawer/review modal) derive their behavior from this predicate.
// Keep this file pure: no UI strings — those belong to the web-app utility that
// wraps `getMatchingTargetStatus`. See spec §3.
Object.defineProperty(exports, "__esModule", { value: true });
exports.isUsedInCapitalizableProjectClustering = exports.getCapitalizableProjectClusteringStatus = exports.CLUSTERING_METHOD = exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = void 0;
const month_year_utils_1 = require("../month-year.utils");
const asc_cluster_type_enum_1 = require("./asc-cluster-type.enum");
// String values match the persisted enum on `asc_projects.capitalizable_project_clustering_override`.
// Kept as a const object instead of a TypeScript enum so this module has no
// dependency on the dirty-data entity layer or the GraphQL codegen output.
exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = {
    INCLUDED: 'INCLUDED',
    EXCLUDED: 'EXCLUDED',
};
// Mirror of `ClusteringMethod` enum values from the GraphQL schema. Kept as string
// literals so this module stays free of dependencies on the codegen output.
exports.CLUSTERING_METHOD = {
    CAPITALIZABLE_PROJECT: 'CAPITALIZABLE_PROJECT',
    DETERMINISTIC: 'DETERMINISTIC',
    SMART: 'SMART',
};
/**
 * Active matching target list (mirrors `AscProjectRepository.findActiveAscProjectMatchTargets`):
 *   1. override = INCLUDED                                                       -> active
 *   2. override IS NULL AND cap_start IS NOT NULL AND
 *      (cap_end IS NULL OR cap_end in/after the last completed ASC sync's
 *      end month) AND activity is current (end_date in/after that month)         -> active (default rule)
 *   3. override IS NULL AND cluster_type = MAIN AND start_date IS NULL           -> active (fresh MAIN)
 * Otherwise inactive. EXCLUDED override always inactive. A capitalizing project whose activity has
 * gone stale (end_date before the boundary month) is 'no-recent-activity' (inactive).
 *
 * `lastCompletedAscSyncPeriodEnd` is the period-end date of the company's last
 * completed ASC sync (`Company.lastCompletedAscSyncPeriodEnd` in GraphQL). A cap_end
 * in/after that month is a "boundary" end date — an auto-set artifact of that sync's
 * data horizon that the next sync may extend — so the project is still an active
 * matching target. When null/omitted, clause 2 requires cap_end IS NULL as before.
 */
const getCapitalizableProjectClusteringStatus = (project, clusteringMethod, lastCompletedAscSyncPeriodEnd) => {
    if (clusteringMethod !== exports.CLUSTERING_METHOD.CAPITALIZABLE_PROJECT) {
        return { isActive: false, reason: 'not-applicable' };
    }
    const override = project.capitalizableProjectClusteringOverride ?? null;
    if (override === exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE.INCLUDED) {
        return { isActive: true, reason: 'included' };
    }
    if (override === exports.CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE.EXCLUDED) {
        return { isActive: false, reason: 'excluded' };
    }
    if (project.capitalizationStartDate) {
        // A capitalizing project stays a matching target only while its activity is current — its last
        // activity month (end_date) must be on/after the last completed sync's end month. Null-safe: no
        // completed sync (boundary null) or no recorded activity (end_date null) keeps it active. A
        // finalized cap_end (capitalization-ended) still wins over stale activity below.
        const hasRecentActivity = lastCompletedAscSyncPeriodEnd == null ||
            project.endDate == null ||
            !(0, month_year_utils_1.isMonthYearBefore)((0, month_year_utils_1.dateToMonthYear)(project.endDate), (0, month_year_utils_1.dateToMonthYear)(lastCompletedAscSyncPeriodEnd));
        if (project.capitalizationEndDate) {
            const isBoundaryCapEnd = lastCompletedAscSyncPeriodEnd != null &&
                !(0, month_year_utils_1.isMonthYearBefore)((0, month_year_utils_1.dateToMonthYear)(project.capitalizationEndDate), (0, month_year_utils_1.dateToMonthYear)(lastCompletedAscSyncPeriodEnd));
            if (isBoundaryCapEnd) {
                return hasRecentActivity
                    ? { isActive: true, reason: 'default-active-boundary-extend' }
                    : { isActive: false, reason: 'no-recent-activity' };
            }
            return { isActive: false, reason: 'capitalization-ended' };
        }
        return hasRecentActivity
            ? { isActive: true, reason: 'default-active' }
            : { isActive: false, reason: 'no-recent-activity' };
    }
    // cap_start IS NULL — check clause 3 (fresh MAIN cluster project without effort data yet).
    if (project.clusterType === asc_cluster_type_enum_1.AscClusterType.CAPITALIZABLE_PROJECT_MAIN && !project.startDate) {
        return { isActive: true, reason: 'default-active-fresh-main' };
    }
    return { isActive: false, reason: 'capitalization-not-started' };
};
exports.getCapitalizableProjectClusteringStatus = getCapitalizableProjectClusteringStatus;
const isUsedInCapitalizableProjectClustering = (project, clusteringMethod, lastCompletedAscSyncPeriodEnd) => (0, exports.getCapitalizableProjectClusteringStatus)(project, clusteringMethod, lastCompletedAscSyncPeriodEnd).isActive;
exports.isUsedInCapitalizableProjectClustering = isUsedInCapitalizableProjectClustering;
